import './node-polyfills';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { playHoverBlip, playInfoCue, playIntroCue, playVersusCue, playWinCue, playWinnerAssetCue } from './fx';
import { applyVote, infoLine, matchupLine, pickPair, STARTING_THINGS, winnerLine, type Thing } from './things';
import { AWESOME_CONFIG, buildVotePaymentTransaction, fetchArena, getProvider, requestVoteNonce, submitDemoVote, submitVerifiedVote, type MatchupStats } from './solana';
import './styles.css';

const DEMO_BALANCE = 1000;
const DATA_VERSION = 'stored-arena-v20-big-bank';


type WalletState = {
  address: string;
  status: 'disconnected' | 'connected';
};

type MatchupRecord = {
  aId: string;
  bId: string;
  aName: string;
  bName: string;
  aWins: number;
  bWins: number;
  votes: number;
  lastWinner: string;
};

type ResultCard = {
  winner: Thing;
  loser: Thing;
  side: 'left' | 'right';
  delta: number;
  rank: number;
  loserRank: number;
  winnerWins: number;
  loserWins: number;
};

function App() {
  const [things, setThings] = useState<Thing[]>(() => {
    const version = localStorage.getItem('awesome.dataVersion');
    const saved = version === DATA_VERSION ? localStorage.getItem('awesome.things') : null;
    if (version !== DATA_VERSION) localStorage.removeItem('awesome.things');
    return saved ? JSON.parse(saved).filter(isArenaWorthy) : [];
  });
  const [pair, setPair] = useState<readonly [string, string]>(() => ['', ''] as const);
  const [balance, setBalance] = useState(() => Number(localStorage.getItem('awesome.balance') ?? DEMO_BALANCE));
  const [lastVote, setLastVote] = useState<string>('The arena is open. Choose wisely.');
  const [matchupCopy, setMatchupCopy] = useState<string>('Tonight: two random nouns enter, one leaves with a fake trophy.');
  const [wallet, setWallet] = useState<WalletState>({ address: '', status: 'disconnected' });
  const [history, setHistory] = useState<MatchupRecord[]>(() => {
    const saved = localStorage.getItem('awesome.matchups');
    return saved ? JSON.parse(saved).filter(isArenaWorthy) : [];
  });
  const [wikiStatus, setWikiStatus] = useState('summoning weird Wikipedia nouns…');
  const [isVoting, setIsVoting] = useState(false);
  const [winnerSplash, setWinnerSplash] = useState<{ name: string; side: 'left' | 'right' } | null>(null);
  const [resultCard, setResultCard] = useState<ResultCard | null>(null);
  const [showVersus, setShowVersus] = useState(false);
  const [recentPairIds, setRecentPairIds] = useState<string[]>([]);
  const activePairRef = useRef<readonly [string, string]>(['', ''] as const);

  useEffect(() => {
    localStorage.setItem('awesome.dataVersion', DATA_VERSION);
    void summonArena(900, true);
  }, []);

  useEffect(() => { activePairRef.current = pair; }, [pair]);

  const left = things.find((t) => t.id === pair[0]) || things[0];
  const right = things.find((t) => t.id === pair[1]) || things[1];
  const leaderboard = useMemo(() => [...things].sort((a, b) => b.score - a.score), [things]);
  const bottomboard = useMemo(() => [...things].sort((a, b) => a.score - b.score), [things]);
  const bestHistory = useMemo(() => [...history].sort((a, b) => b.votes - a.votes).slice(0, 5), [history]);
  const dominantThings = useMemo(() => {
    const map = new Map<string, { name: string; wins: number; losses: number }>();
    for (const h of history) {
      const a = map.get(h.aId) || { name: h.aName, wins: 0, losses: 0 };
      const b = map.get(h.bId) || { name: h.bName, wins: 0, losses: 0 };
      a.wins += h.aWins; a.losses += h.bWins;
      b.wins += h.bWins; b.losses += h.aWins;
      map.set(h.aId, a); map.set(h.bId, b);
    }
    return [...map.values()].sort((a, b) => (b.wins - b.losses) - (a.wins - a.losses));
  }, [history]);
  const totalVotes = things.reduce((sum, t) => sum + t.wins + t.losses, 0);
  const realModeReady = AWESOME_CONFIG.mode === 'solana' && Boolean(AWESOME_CONFIG.tokenMint && AWESOME_CONFIG.treasuryWallet);
  const [showIntro, setShowIntro] = useState(() => !localStorage.getItem('awesome.introPlayed'));

  function startShow() {
    localStorage.setItem('awesome.introPlayed', '1');
    setShowIntro(false);
    playIntroCue();
  }

  function persistDemo(nextThings: Thing[], nextBalance = balance) {
    localStorage.setItem('awesome.things', JSON.stringify(nextThings));
    localStorage.setItem('awesome.balance', String(nextBalance));
  }

  function revealMatchup(a: Thing, b: Thing, withSound = true) {
    setMatchupCopy(matchupLine(a, b));
    if (withSound) {
      setShowVersus(true);
      window.setTimeout(() => setShowVersus(false), 900);
      window.setTimeout(() => playVersusCue(), 120);
    }
  }

  async function summonArena(count = 80, replaceStale = false, avoidIds: string[] = []) {
    setWikiStatus('pulling stored contenders from the arena vault…');
    try {
      const data = await fetchArena(count);
      if (!data?.things?.length) throw new Error('empty arena batch');
      setThings((current) => {
        const base = replaceStale ? current.filter(isArenaWorthy).slice(0, 1100) : current.filter(isArenaWorthy);
        const merged = mergeThings(base, data.things.filter(isArenaWorthy));
        localStorage.setItem('awesome.things', JSON.stringify(merged));
        const hasActivePair = activePairRef.current[0] && activePairRef.current[1];
        if (replaceStale || !hasActivePair) {
          const nextPair = pickFreshPair(merged, avoidIds.length ? avoidIds : recentPairIds);
          activePairRef.current = nextPair;
          setPair(nextPair);
          const a = merged.find((t) => t.id === nextPair[0]);
          const b = merged.find((t) => t.id === nextPair[1]);
          if (a && b) revealMatchup(a, b, false);
        }
        return merged;
      });
      setWikiStatus(`${data.source === 'stored-d1' ? 'D1 arena' : 'curated fallback'} loaded ${data.things.length} contenders. Wikipedia is no longer on the hot path.`);
    } catch {
      setWikiStatus('Arena vault hiccuped; using cached local fighters for now.');
    }
  }

  async function connectWallet() {
    const provider = getProvider();
    if (!provider) {
      setLastVote('No Solana wallet found. Install Phantom/Backpack, then refresh.');
      return;
    }
    const connected = await provider.connect();
    setWallet({ address: connected.publicKey.toBase58(), status: 'connected' });
    setLastVote(`Wallet connected: ${short(connected.publicKey.toBase58())}`);
  }

  async function vote(winnerId: string, loserId: string, side: 'left' | 'right') {
    if (isVoting || resultCard) return;
    const winningThing = things.find((t) => t.id === winnerId);
    setWinnerSplash({ name: winningThing?.name || 'WINNER', side });
    playWinCue(side);
    setTimeout(playWinnerAssetCue, 380);
    if (realModeReady) return voteOnSolana(winnerId, loserId, side);
    return voteDemo(winnerId, loserId, side);
  }

  async function voteDemo(winnerId: string, loserId: string, side: 'left' | 'right') {
    if (balance < AWESOME_CONFIG.voteCostUi) {
      setLastVote('Out of demo $AWESOME. Faucet refilled you. Real mode uses wallet token transfers.');
      setBalance(DEMO_BALANCE);
      localStorage.setItem('awesome.balance', String(DEMO_BALANCE));
      return;
    }

    setIsVoting(true);
    const winner = things.find((t) => t.id === winnerId)!;
    const loser = things.find((t) => t.id === loserId)!;
    const nextBalance = balance - AWESOME_CONFIG.voteCostUi;
    try {
      const sessionId = getDemoSessionId();
      const result = await submitDemoVote({ winnerId, loserId, sessionId });
      const merged = mergeThings(things, result.things);
      setThings(merged);
      setBalance(nextBalance);
      persistDemo(merged, nextBalance);
      const matchup = result.matchup;
      recordMatchup(winner, loser, matchup);
      const hh = matchupToHeadToHead(matchup, winner, loser) || recordMatchupPreview(winner, loser);
      const ranks = rankMap(merged);
      const rank = ranks.get(winnerId) || 0;
      const loserRank = ranks.get(loserId) || 0;
      setLastVote(result.message || winnerLine(winner, loser, result.delta));
      setResultCard({ winner, loser, side, delta: result.delta, rank, loserRank, winnerWins: hh.winnerWins, loserWins: hh.loserWins });
    } catch {
      const { things: nextThings, delta } = applyVote(things, winnerId, loserId);
      setThings(nextThings);
      setBalance(nextBalance);
      persistDemo(nextThings, nextBalance);
      const hh = recordMatchupPreview(winner, loser);
      recordMatchup(winner, loser);
      const ranks = rankMap(nextThings);
      const rank = ranks.get(winnerId) || 0;
      const loserRank = ranks.get(loserId) || 0;
      setLastVote(`${winnerLine(winner, loser, delta)} Local fallback — API vote store unavailable.`);
      setResultCard({ winner, loser, side, delta, rank, loserRank, winnerWins: hh.winnerWins, loserWins: hh.loserWins });
    } finally {
      setIsVoting(false);
    }
  }

  async function voteOnSolana(winnerId: string, loserId: string, side: 'left' | 'right') {
    const provider = getProvider();
    if (!provider) {
      setLastVote('Need Phantom/Backpack wallet for real $AWESOME voting.');
      return;
    }
    setIsVoting(true);
    try {
      const connected = provider.publicKey ? { publicKey: provider.publicKey } : await provider.connect();
      const address = connected.publicKey.toBase58();
      setWallet({ address, status: 'connected' });
      setLastVote('Creating secure vote intent…');
      const intent = await requestVoteNonce(address, winnerId, loserId);
      setLastVote(`Sign ${AWESOME_CONFIG.voteCostUi} $AWESOME vote payment on ${AWESOME_CONFIG.cluster}…`);
      const { tx, connection } = await buildVotePaymentTransaction({ voter: connected.publicKey, winnerId, loserId, nonce: intent.nonce });
      const signed = await provider.signTransaction(tx);
      const signature = await connection.sendRawTransaction(signed.serialize(), { skipPreflight: false });
      setLastVote(`Payment sent. Verifying ${short(signature)}…`);
      await connection.confirmTransaction(signature, 'confirmed');
      const result = await submitVerifiedVote({ wallet: address, winnerId, loserId, nonce: intent.nonce, signature });
      setThings(result.things);
      const nextPair = pickFreshPair(result.things, recentPairIds);
      activePairRef.current = nextPair;
      setPair(nextPair);
      const a = result.things.find((t: Thing) => t.id === nextPair[0]);
      const b = result.things.find((t: Thing) => t.id === nextPair[1]);
      if (a && b) revealMatchup(a, b);
      const winner = things.find((t) => t.id === winnerId);
      const loser = things.find((t) => t.id === loserId);
      if (winner && loser) {
        const hh = matchupToHeadToHead(result.matchup, winner, loser) || recordMatchupPreview(winner, loser);
        recordMatchup(winner, loser, result.matchup);
        const ranks = rankMap(result.things);
        const rank = ranks.get(winnerId) || 0;
        const loserRank = ranks.get(loserId) || 0;
        setResultCard({ winner, loser, side, delta: result.delta, rank, loserRank, winnerWins: hh.winnerWins, loserWins: hh.loserWins });
      }
      setLastVote(`${side === 'left' ? 'Thing one' : 'Thing two'} wins! ${result.message}`);
    } catch (err) {
      setLastVote(err instanceof Error ? err.message : 'Vote failed');
    } finally {
      setIsVoting(false);
    }
  }

  function resetArena() {
    localStorage.removeItem('awesome.things');
    localStorage.removeItem('awesome.matchups');
    setHistory([]);
    setThings([]);
    setBalance(DEMO_BALANCE);
    setLastVote('Local demo wiped. Pulling a fresh Wikipedia circus now.');
    localStorage.setItem('awesome.balance', String(DEMO_BALANCE));
    void summonArena(900, true);
  }

  function continueToNextVote() {
    const avoid = resultCard ? [...recentPairIds, resultCard.winner.id, resultCard.loser.id].slice(-80) : recentPairIds;
    setRecentPairIds(avoid);
    setWinnerSplash(null);
    setResultCard(null);
    const storedCount = things.length;
    if (storedCount < 700 || Math.random() < 0.18) void summonArena(300, false, avoid);
    if (things.length >= 2) {
      const nextPair = pickFreshPair(things, avoid);
      activePairRef.current = nextPair;
      setPair(nextPair);
      const a = things.find((t) => t.id === nextPair[0]);
      const b = things.find((t) => t.id === nextPair[1]);
      if (a && b) revealMatchup(a, b, false);
    }
  }

  function recordMatchupPreview(winner: Thing, loser: Thing) {
    const [a, b] = [winner, loser].sort((x, y) => x.id.localeCompare(y.id));
    const h = history.find((row) => row.aId === a.id && row.bId === b.id);
    const aWins = (h?.aWins || 0) + (winner.id === a.id ? 1 : 0);
    const bWins = (h?.bWins || 0) + (winner.id === b.id ? 1 : 0);
    return { winnerWins: winner.id === a.id ? aWins : bWins, loserWins: winner.id === a.id ? bWins : aWins };
  }

  function recordMatchup(winner: Thing, loser: Thing, serverStats?: MatchupStats) {
    const [a, b] = [winner, loser].sort((x, y) => x.id.localeCompare(y.id));
    setHistory((current) => {
      const key = `${a.id}::${b.id}`;
      const next = [...current];
      const idx = next.findIndex((h) => `${h.aId}::${h.bId}` === key);
      const serverRow = serverStats ? {
        aId: serverStats.a_id,
        bId: serverStats.b_id,
        aName: serverStats.a_id === winner.id ? winner.name : loser.name,
        bName: serverStats.b_id === winner.id ? winner.name : loser.name,
        aWins: serverStats.a_wins,
        bWins: serverStats.b_wins,
        votes: serverStats.votes,
        lastWinner: serverStats.last_winner_name || winner.name
      } : null;
      if (idx >= 0) {
        const h = next[idx];
        next[idx] = serverRow || {
          ...h,
          aWins: h.aWins + (winner.id === h.aId ? 1 : 0),
          bWins: h.bWins + (winner.id === h.bId ? 1 : 0),
          votes: h.votes + 1,
          lastWinner: winner.name
        };
      } else {
        next.push(serverRow || { aId: a.id, bId: b.id, aName: a.name, bName: b.name, aWins: winner.id === a.id ? 1 : 0, bWins: winner.id === b.id ? 1 : 0, votes: 1, lastWinner: winner.name });
      }
      const trimmed = next.sort((x, y) => y.votes - x.votes).slice(0, 250);
      localStorage.setItem('awesome.matchups', JSON.stringify(trimmed));
      return trimmed;
    });
  }

  return (
    <main>
      {showIntro && (
        <button className="intro-gate" onClick={startShow}>
          <span>CLICK TO ENTER</span>
          <strong>THE AWESOMEST THING EVER</strong>
          <em>sound on · vote hard</em>
        </button>
      )}
      <section className="hero">
        <div className="logo-stack">
          <span>THE MOST</span>
          <strong>AWESOMEST</strong>
          <span>THING EVER</span>
        </div>
        <div className="chrome-beams" />
        <div className="scanline" />
        <div className="eyebrow">awesomestthingever.com · $AWESOME voting protocol</div>
        <h1>Which thing is more awesome?</h1>
        <div className="hero-tabs"><span>MOST AWESOME</span><span>BATTLE</span><span>MOST INADEQUATE</span></div>
        <p className="tagline">The world’s largest database of awesomeness: people, places, and objects battling until only one thing is the most awesomest thing ever.</p>
        <div className="stats">
          <span>{totalVotes.toLocaleString()} total votes</span>
          <span>{realModeReady ? `${AWESOME_CONFIG.cluster} real-token mode` : `${balance.toLocaleString()} demo $AWESOME`}</span>
          <span>{leaderboard[0]?.name || 'Wikipedia loading…'} is #1</span>
          <span>{things.filter((t) => t.id.startsWith('wiki-')).length} wiki freaks loaded</span>
        </div>
        <div className="wallet-row">
          <button onClick={connectWallet}>{wallet.status === 'connected' ? short(wallet.address) : 'connect wallet'}</button>
          <span>{realModeReady ? `Mint ${short(AWESOME_CONFIG.tokenMint)} · treasury ${short(AWESOME_CONFIG.treasuryWallet)}` : 'Demo mode until devnet/mainnet mint + treasury are configured.'}</span>
        </div>
      </section>

      <div className="news-burst">264,000 votes in 4 hours · 18 votes per second · dangerously addictive</div>
      <div className="matchup-copy">{matchupCopy}</div>
      <div className="wiki-loader"><span>{wikiStatus}</span><button onClick={() => summonArena(300)}>load arena batch</button></div>
      <div className="spotlight">BATTLE</div>
      <section className={`arena ${winnerSplash ? `has-winner winner-${winnerSplash.side}` : ''}`} aria-label="Voting arena">
        <div className="stage-depth" />
        {winnerSplash && !resultCard && (
          <div className="winner-card impact-card">
            <span>THING {winnerSplash.side === 'left' ? 'ONE' : 'TWO'} WINS!</span>
            <strong>{winnerSplash.name}</strong>
            <em>DESTROYED THE COMPETITION</em>
          </div>
        )}
        {resultCard && <VoteResultCard result={resultCard} onContinue={continueToNextVote} />}
        {showVersus && <div className="versus-bumper"><span>VERSUS!</span><strong>BATTLE MODE</strong></div>}
        {left && right ? (<>
          <ThingCard thing={left} side="left" disabled={isVoting} onVote={() => vote(left.id, right.id, 'left')} />
          <div className="versus">
            <div>VS</div>
            <span>{AWESOME_CONFIG.voteCostUi} $AWESOME</span>
          </div>
          <ThingCard thing={right} side="right" disabled={isVoting} onVote={() => vote(right.id, left.id, 'right')} />
        </>) : <div className="loading-arena">Wikipedia is loading fresh fighters with actual images…</div>}
      </section>

      <section className="ticker"><strong>ABC NEWS STYLE UPDATE:</strong> {lastVote}</section>

      <section className="all-time-wrap dual-rankboards">
        <div className="all-time-board">
          <div className="rank-label">RANK</div>
          <div className="all-time-title">ALL TIME AWESOMEST</div>
          {leaderboard.slice(0, 10).map((thing, index) => (
            <div className="all-time-row" key={thing.id}>
              <span>{index + 1}.</span>
              <strong>{thing.name}</strong>
              <em>{thing.score}</em>
            </div>
          ))}
        </div>
        <div className="all-time-board not-awesome-board">
          <div className="rank-label">LOWEST RANK</div>
          <div className="all-time-title">ALL TIME NOT AWESOMEST</div>
          {bottomboard.slice(0, 10).map((thing, index) => (
            <div className="all-time-row" key={thing.id}>
              <span>{leaderboard.length - index}.</span>
              <strong>{thing.name}</strong>
              <em>{thing.score}</em>
            </div>
          ))}
        </div>
        <button className="reset-demo-button" onClick={resetArena}>reset demo</button>
      </section>

      <section className="grid simple-grid">
        <div className="panel matchup-history">
          <h2>Head-to-Head Hell</h2>
          {bestHistory.length ? bestHistory.map((h) => (
            <div className="history-row" key={`${h.aId}-${h.bId}`}>
              <strong>{h.aName}</strong>
              <span>{h.aWins} - {h.bWins}</span>
              <strong>{h.bName}</strong>
              <em>last humiliation: {h.lastWinner}</em>
            </div>
          )) : <p>No grudges recorded yet. Click something and make history worse.</p>}
          <div className="mini-boards">
            <div><h3>Most Dominant</h3>{dominantThings.slice(0, 4).map((t) => <p key={t.name}>{t.name}: +{t.wins - t.losses}</p>)}</div>
            <div><h3>Most Humiliated</h3>{dominantThings.slice(-4).reverse().map((t) => <p key={t.name}>{t.name}: {t.wins - t.losses}</p>)}</div>
          </div>
        </div>

        <div className="panel manifesto">
          <h2>Awesomeness Engine</h2>
          <ul>
            <li>Two random contenders enter the battle.</li>
            <li>Users vote what is objectively more awesome.</li>
            <li>Winners climb the global awesome rank.</li>
            <li>Stored contender batches keep voting fast; Wikipedia/Wikidata enrichment runs off the hot path.</li>
            <li>Every matchup records a head-to-head grudge record.</li>
            <li>$AWESOME turns voting wars into token-powered internet combat.</li>
          </ul>
          <div className="cta-row">
            <a href="https://pump.fun" target="_blank" rel="noreferrer">Pump.fun launch path</a>
            <a href="https://www.wikidata.org" target="_blank" rel="noreferrer">Wikidata seed source</a>
          </div>
        </div>
      </section>
    </main>
  );
}

function ThingCard({ thing, side, disabled, onVote }: { thing: Thing; side: 'left' | 'right'; disabled: boolean; onVote: () => void }) {
  const winRate = Math.round((thing.wins / Math.max(1, thing.wins + thing.losses)) * 100);
  const [imageUrl, setImageUrl] = useState(thing.imageUrl || '');

  useEffect(() => {
    let cancelled = false;
    setImageUrl(thing.imageUrl || '');
    if (thing.imageUrl) return;
    const title = thing.wikiTitle || thing.name;
    fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const src = data?.thumbnail?.source || data?.originalimage?.source || '';
        if (!cancelled && src) setImageUrl(src);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [thing.id, thing.imageUrl, thing.wikiTitle, thing.name]);

  return (
    <article className={`contestant ${side}`}>
      <div className="brick-pillar" />
      <div className="placard">{side === 'left' ? 'THING ONE' : 'THING TWO'}</div>
      <button className="vote-board" disabled={disabled} onMouseEnter={playHoverBlip} onClick={onVote}>
        <div className="image-box">
          {imageUrl && <img src={imageUrl} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} alt={thing.name} />}
          <div className="image-fallback">{thing.emoji}</div>
        </div>
        <div className="caption-strip">{thing.category}</div>
        <h2>{thing.name}</h2>
        <p>{thing.blurb}</p>
        <div className="metrics">
          <span>{thing.score} awesome</span>
          <span>{winRate}% wins</span>
        </div>
        <div className="board-actions">
          <span className="vote-copy">{disabled ? 'VERIFYING…' : 'CLICK IF MORE AWESOME'}</span>
          <span className="what-is-this" onMouseEnter={playHoverBlip} onClick={(e) => { e.stopPropagation(); playInfoCue(); alert(infoLine(thing)); }}>WHAT IS THIS?</span>
        </div>
      </button>
      <div className="brick-pillar" />
    </article>
  );
}


function VoteResultCard({ result, onContinue }: { result: ResultCard; onContinue: () => void }) {
  const [imageUrl, setImageUrl] = useState(result.winner.imageUrl || '');
  useEffect(() => {
    let cancelled = false;
    setImageUrl(result.winner.imageUrl || '');
    if (result.winner.imageUrl) return;
    fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(result.winner.wikiTitle || result.winner.name)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const src = data?.thumbnail?.source || data?.originalimage?.source || '';
        if (!cancelled && src) setImageUrl(src);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [result.winner.id, result.winner.imageUrl, result.winner.wikiTitle, result.winner.name]);

  return (
    <div className="result-card">
      <div className="result-image">
        {imageUrl ? <img src={imageUrl} alt={result.winner.name} /> : <span>{result.winner.emoji}</span>}
      </div>
      <div className="result-copy">
        <span>THING {result.side === 'left' ? 'ONE' : 'TWO'} WINS</span>
        <strong>{result.winner.name}</strong>
        <em>{result.loser.name} got smashed into the loser dimension.</em>
        <div className="result-stats tale-of-tape">
          <div className="record-card winner-record">
            <span>WIN/LOSS RECORD</span>
            <strong>{result.winner.wins.toLocaleString()} - {result.winner.losses.toLocaleString()}</strong>
            <small>RANK</small>
            <em>{ordinal(result.rank || 0)}</em>
            <b>MOST AWESOMEST</b>
          </div>
          <div className="record-card loser-record">
            <span>{result.loser.name}</span>
            <strong>{result.loser.wins.toLocaleString()} - {result.loser.losses.toLocaleString()}</strong>
            <small>RANK</small>
            <em>{ordinal(result.loserRank || 0)}</em>
            <b>MOST AWESOMEST</b>
          </div>
          <div className="versus-record">HEAD-TO-HEAD {result.winnerWins} - {result.loserWins} · +{result.delta}</div>
        </div>
        <button onClick={onContinue}>NEXT RIDICULOUS FIGHT</button>
      </div>
    </div>
  );
}

function rankMap(things: Thing[]) {
  const ranks = new Map<string, number>();
  [...things].sort((a, b) => b.score - a.score).forEach((thing, index) => ranks.set(thing.id, index + 1));
  return ranks;
}

function ordinal(value: number) {
  if (!value) return '?';
  const mod100 = value % 100;
  const suffix = mod100 >= 11 && mod100 <= 13 ? 'th' : value % 10 === 1 ? 'st' : value % 10 === 2 ? 'nd' : value % 10 === 3 ? 'rd' : 'th';
  return `${value}${suffix}`;
}

function matchupToHeadToHead(matchup: MatchupStats | undefined, winner: Thing, loser: Thing) {
  if (!matchup) return null;
  const winnerWins = matchup.a_id === winner.id ? matchup.a_wins : matchup.b_wins;
  const loserWins = matchup.a_id === loser.id ? matchup.a_wins : matchup.b_wins;
  return { winnerWins, loserWins };
}

function getDemoSessionId() {
  const key = 'awesome.demoSessionId';
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const next = crypto.randomUUID();
  localStorage.setItem(key, next);
  return next;
}

function pickFreshPair(things: Thing[], avoidIds: string[]) {
  const worthy = things.filter(isArenaWorthy);
  const fresh = worthy.filter((t) => !avoidIds.includes(t.id));
  const pool = fresh.length >= 2 ? fresh : worthy.length >= 2 ? worthy : things.filter(isArenaWorthy);
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  const band = shuffled.slice(0, Math.min(shuffled.length, 180));
  return pickPair(band.length >= 2 ? band : pool);
}

function isArenaWorthy(t: Thing) {
  const text = `${t.name} ${t.category} ${(t.tags || []).join(' ')} ${t.blurb}`.toLowerCase();
  if (/\b(village|municipality|township|commune|county|district|constituenc|election|electoral|politician|parliament|council|ward|province|prefecture|oblast|raion|canton|diocese|station)\b/.test(text)) return false;
  if (/\b(plant|flower|genus|species of|moth|beetle|algae|fungus|fern|grass|shrub|herbaceous)\b/.test(text)) return false;
  return /nintendo|mario|zelda|sega|playstation|xbox|game|arcade|time travel|black hole|godzilla|monster|dragon|alien|robot|terminator|samurai|ninja|pirate|wizard|lightsaber|sword|laser|volcano|shark|dinosaur|tyrannosaurus|pizza|taco|burger|hot dog|chocolate|bacon|sushi|coffee|star wars|batman|spider-man|matrix|doom|mortal kombat|street fighter|sonic|pokemon|jurassic|myth|kraken|vampire|werewolf|zombie|ghost|rocket|moon|mars|cyberpunk|boombox|music|breakbeat|guitar|wrestling|skateboarding|hoverboard|jet pack/.test(text);
}

function mergeThings(current: Thing[], incoming: Thing[]) {
  const byId = new Map<string, Thing>();
  for (const item of current.filter(isArenaWorthy)) byId.set(item.id, item);
  for (const item of incoming.filter(isArenaWorthy)) {
    const existing = byId.get(item.id);
    byId.set(item.id, existing ? { ...item, score: existing.score, wins: existing.wins, losses: existing.losses } : item);
  }
  return [...byId.values()].sort((a, b) => b.score - a.score).slice(0, 1100);
}

function short(value: string) {
  if (!value) return 'unset';
  return `${value.slice(0, 4)}…${value.slice(-4)}`;
}

createRoot(document.getElementById('root')!).render(<App />);
